import { IStepGeometry } from '@stair-craft/common/util';

/*
 * The marks a spiral's top view sets inside its circle: the step numbers and the walking-line
 * arrow. The treads of a spiral overlap like a fan, and over a full turn the last ones come over
 * the first, so the radial edges of other treads cross every tread. A mark goes where the edges
 * leave room, not at the middle of its own tread.
 */

/** A point of the plan in stair-local millimetres. */
export interface ISpiralPoint {
    x: number;
    z: number;
}

type ISpiralCorners = NonNullable<IStepGeometry['winderCorners']>;

/** Where between the inner and the outer edge a number stands: 0 at the inner edge, 1 at the outer one. */
const NUMBER_RADIUS_SHARE: number = 0.6;
/** Where the walking-line arrow runs, same measure: past the numbers, clear of them. */
const WALK_RADIUS_SHARE: number = 0.88;
/** How many treads the walking-line arrow runs over from the first one. */
const WALK_STEPS: number = 2;
/** Points per tread on the walking-line arc. */
const WALK_POINTS_PER_STEP: number = 6;
/** An edge this close to a tread's own edge, as a share of the tread's sweep, is that edge. */
const SAME_EDGE_SHARE: number = 0.02;
/** Below this the two edges of a tread are taken for parallel. */
const PARALLEL_EPSILON: number = 1e-9;

/** Where two lines meet, each through two points; null for parallel ones. */
function meet(a: ISpiralPoint, b: ISpiralPoint, c: ISpiralPoint, d: ISpiralPoint): ISpiralPoint | null {
    const denominator: number = (a.x - b.x) * (c.z - d.z) - (a.z - b.z) * (c.x - d.x);
    if (Math.abs(denominator) < PARALLEL_EPSILON) {
        return null;
    }
    const t: number = ((a.x - c.x) * (c.z - d.z) - (a.z - c.z) * (c.x - d.x)) / denominator;
    return { x: a.x + t * (b.x - a.x), z: a.z + t * (b.z - a.z) };
}

function angleOf(point: ISpiralPoint, pole: ISpiralPoint): number {
    return Math.atan2(point.z - pole.z, point.x - pole.x);
}

function distance(a: ISpiralPoint, b: ISpiralPoint): number {
    return Math.hypot(a.x - b.x, a.z - b.z);
}

/** An angle brought into (−π, π]. */
function wrapped(angle: number): number {
    const turn: number = 2 * Math.PI;
    return angle - turn * Math.ceil((angle - Math.PI) / turn);
}

function pointAt(pole: ISpiralPoint, angle: number, radius: number): ISpiralPoint {
    return { x: pole.x + radius * Math.cos(angle), z: pole.z + radius * Math.sin(angle) };
}

function radiusAt(corners: ISpiralCorners, pole: ISpiralPoint, share: number): number {
    const inner: number = distance(corners.innerFront, pole);
    return inner + share * (distance(corners.outerFront, pole) - inner);
}

function treadsOf(steps: readonly IStepGeometry[]): ISpiralCorners[] {
    return steps
        .map((step: IStepGeometry): ISpiralCorners | undefined => step.winderCorners)
        .filter((corners: ISpiralCorners | undefined): corners is ISpiralCorners => corners !== undefined);
}

/** The pole of a spiral: where the front and the back edge of its first tread meet. */
export function spiralPole(steps: readonly IStepGeometry[]): ISpiralPoint | null {
    const corners: ISpiralCorners | undefined = treadsOf(steps).at(0);
    return corners ? meet(corners.innerFront, corners.outerFront, corners.innerBack, corners.outerBack) : null;
}

/** The widest stretch between neighbouring cuts of [0, 1]. */
function widestGap(cuts: readonly number[]): [number, number] {
    const bounds: number[] = [0, ...[...cuts].sort((a: number, b: number): number => a - b), 1];
    let best: [number, number] = [0, 0];
    bounds.slice(1).forEach((to: number, index: number): void => {
        if (to - bounds[index] > best[1] - best[0]) {
            best = [bounds[index], to];
        }
    });
    return best;
}

/**
 * Where a tread's number stands: in the widest stretch of the tread between the radial edges of
 * all the treads drawn, at a fixed share of the tread's radius.
 */
export function spiralNumberAt(corners: ISpiralCorners, steps: readonly IStepGeometry[], pole: ISpiralPoint): ISpiralPoint {
    const front: number = angleOf(corners.outerFront, pole);
    const sweep: number = wrapped(angleOf(corners.outerBack, pole) - front);
    const cuts: number[] = treadsOf(steps)
        .flatMap((tread: ISpiralCorners): ISpiralPoint[] => [tread.outerFront, tread.outerBack])
        .map((edge: ISpiralPoint): number => wrapped(angleOf(edge, pole) - front) / sweep)
        .filter((share: number): boolean => share > SAME_EDGE_SHARE && share < 1 - SAME_EDGE_SHARE);
    const [from, to]: [number, number] = widestGap(cuts);
    return pointAt(pole, front + (sweep * (from + to)) / 2, radiusAt(corners, pole, NUMBER_RADIUS_SHARE));
}

/**
 * The walking-line arrow of a spiral: an arc from the first tread's front edge over the first
 * treads in the direction of climb, outside the numbers' radius. Empty without two treads.
 */
export function spiralWalkArc(steps: readonly IStepGeometry[], pole: ISpiralPoint): ISpiralPoint[] {
    const treads: ISpiralCorners[] = treadsOf(steps);
    if (treads.length < 2) {
        return [];
    }
    const start: number = angleOf(treads[0].outerFront, pole);
    const perStep: number = wrapped(angleOf(treads[1].outerFront, pole) - start);
    const radius: number = radiusAt(treads[0], pole, WALK_RADIUS_SHARE);
    const count: number = WALK_STEPS * WALK_POINTS_PER_STEP;
    return Array.from({ length: count + 1 }, (_unused: unknown, index: number): ISpiralPoint =>
        pointAt(pole, start + (perStep * WALK_STEPS * index) / count, radius)
    );
}
